import { useContext } from 'react';
import {
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  IconButton,
  Grid,
  Autocomplete
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { filter, some, uniqueId } from "lodash";
import { useQuery } from '@apollo/client/react';
import { Activity, ProjectActivitiesProps } from './types';
import SectionHeading from './Notes/SectionHeading';
import CurrentUserContext from '@/Contexts/CurrentUser';
import { EmploymentStatusEnum, GetManagersDocument } from '@/graphql/generated/graphql';

const BLANK_ACTIVITY = {
  id: "",
  title: 'Untitled Activity',
  description: '',
  startTime: '',
  endTime: '',
  supervisorId: null,
  colorCode: '#cccccc'
}

const ProjectActivities: React.FC<ProjectActivitiesProps> = ({ activities, setActivities }) => {
  const { currentUser } = useContext(CurrentUserContext);

  const { data } = useQuery(GetManagersDocument, {
    skip: !currentUser?.defaultCompany?.id,
  });

  const addActivity = () => {
    setActivities([{ ...BLANK_ACTIVITY, id: uniqueId() }, ...activities]);
  };

  const removeActivity = (id: string) => {
    const updated = activities.filter(a => a.id !== id)
    setActivities(updated);
  };

  const updateActivity = (id: string, field: keyof Activity, value: string | null) => {
    setActivities(activities.map(a =>
      a.id === id ? { ...a, [field]: value } : a
    ));
  };

  const handleTitleChange = (index: number, newTitle: string) => {
    const updated = [...activities];
    updated[index] = { ...updated[index], title: newTitle };
    setActivities(updated);
  }

  const shouldDisableAddNew = some((activities), activity => activity.startTime === "" || activity.endTime === "");
  const supervisorOptions = filter(data?.getManagers || [], (manager) => manager.user?.companyEmployment?.status === EmploymentStatusEnum.Active);

  return (
    <Grid size={12} container>
      <Button
        variant="outlined"
        onClick={addActivity}
        startIcon={<AddIcon />}
        disabled={shouldDisableAddNew}
        sx={{
          flex: 1,
          marginBottom: 2,
          borderColor: '#007ACC',
          color: '#003366',
          '&:hover': {
            borderColor: '#005999',
            bgcolor: '#E6F3FF'
          }
        }}
      >
        Add Another Activity
      </Button>
      <Grid size={12} container flexDirection='row' gap={3} data-testid="kickoff-activities-container">
        {activities.map((activity, index) => (
          <Card
            sx={{ maxWidth: 400, border: `5px solid ${activity.colorCode}`, marginBottom: 1 }}
            elevation={0}
            data-testid={`kickoff-activity-${activity.id}`}
            key={`kickoff-activity-${activity.id}`}
          >
            <IconButton
              sx={{
                float: 'right', color: '#999', marginTop: 1,
                '&:hover': {
                  color: '#ef5350',
                  bgcolor: '#ffebee'
                }
              }}
              onClick={() => removeActivity(activity.id)}
            >
              <DeleteIcon />
            </IconButton>
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <SectionHeading
                  sectionName={activity.title}
                  handleTitleChange={(newTitle: string) => handleTitleChange(index, newTitle)}
                />
                <TextField
                  label="Description"
                  value={activity.description}
                  onChange={(e) => updateActivity(activity.id, 'description', e.target.value)}
                  placeholder="Add a description..."
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  size="small"
                  sx={{ marginTop: 1 }}
                />
              </Box>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TextField
                    label="Start Date"
                    type="date"
                    required
                    value={activity.startTime}
                    onChange={(e) => updateActivity(activity.id, 'startTime', e.target.value)}
                    fullWidth
                    error={activity.startTime === ''}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    label="End Date"
                    type="date"
                    required
                    value={activity.endTime}
                    onChange={(e) => updateActivity(activity.id, 'endTime', e.target.value)}
                    fullWidth
                    error={activity.endTime === ''}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid size={8}>
                  {data?.getManagers && (
                    <Autocomplete
                      id={`supervisor-selector-${index}`}
                      options={supervisorOptions}
                      autoHighlight
                      fullWidth
                      value={supervisorOptions.find(manager => manager.user?.companyEmployment?.id === activity.supervisorId) || null}
                      onChange={(_, newValue) => updateActivity(activity.id, 'supervisorId', newValue?.user?.companyEmployment?.id || null)}
                      getOptionLabel={(option) => option.name}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Supervisor"
                          slotProps={{
                            htmlInput: {
                              ...params.inputProps,
                              autoComplete: "new-supervisor",
                            }
                          }}
                        />
                      )}
                    />
                  )}
                </Grid>
                <Grid size={4}>
                  <input
                    type="color"
                    value={activity.colorCode}
                    onChange={(e) => updateActivity(activity.id, 'colorCode', e.target.value)}
                    style={{
                      height: '40px',
                      width: '64px',
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0',
                      cursor: 'pointer'
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Grid>
    </Grid>
  );
}

export default ProjectActivities;
